<template>
  <div class="min-h-screen bg-gradient-to-br from-[#10103c] via-[#1a1a5c] to-[#2a2a7c]">

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-8 pt-32">
        <h2 class="text-3xl font-bold text-white mb-2">Admin Dashboard</h2>
        <p class="text-white/70">Member analytics and application management</p>
      </div>

      <!-- Navigation Tabs -->
      <div class="mb-8">
        <nav class="flex space-x-8">
          <button
            @click="activeTab = 'analytics'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/70 hover:text-white hover:border-white/30'
            ]"
          >
            Analytics Dashboard
          </button>
          <button
            @click="activeTab = 'users'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'users'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/70 hover:text-white hover:border-white/30'
            ]"
          >
            User Management
          </button>
        </nav>
      </div>

      <!-- Analytics Tab -->
      <div v-if="activeTab === 'analytics'">
        <AdminAnalytics />
      </div>

      <!-- Users Tab -->
      <div v-else-if="activeTab === 'users'">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
        <p class="text-red-400">{{ error }}</p>
        <button 
          @click="fetchUsers" 
          class="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>

      <!-- Users Table -->
      <div v-else class="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden">
        <div class="px-6 py-4 border-b border-white/20">
          <h3 class="text-lg font-semibold text-white">
            All Users ({{ users.length }})
          </h3>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-white/5">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  User
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Profile Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Application Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Channel
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Joined
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/10">
              <template v-for="user in users" :key="user.id">
                <!-- Main Row -->
                <tr 
                  class="hover:bg-white/5 transition-colors cursor-pointer"
                  @click="toggleUserDetails(user.id)"
                >
                  <td class="px-6 py-4">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-10 w-10">
                        <div class="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                          <span class="text-white font-medium">
                            {{ getUserInitials(user) }}
                          </span>
                        </div>
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-white">
                          {{ user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'No Name' }}
                        </div>
                        <div class="text-sm text-white/70">{{ user.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <span 
                      :class="[
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                        user.profileCompleted 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      ]"
                    >
                      {{ user.profileCompleted ? 'Complete' : 'Incomplete' }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <span 
                      :class="[
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                        getApplicationStatusClass(user)
                      ]"
                    >
                      {{ getApplicationStatusText(user) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-sm">
                    <template v-if="user.youtubeChannel && isValidUrl(user.youtubeChannel)">
                      <a 
                        :href="user.youtubeChannel" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="text-blue-400 hover:text-blue-300 underline transition-colors break-all inline-flex items-center gap-1"
                        @click.stop
                      >
                        {{ formatChannelDisplay(user.youtubeChannel) }}
                        <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </template>
                    <template v-else>
                      <span class="text-white/70">{{ user.youtubeChannel || 'Not provided' }}</span>
                    </template>
                  </td>
                  <td class="px-6 py-4 text-sm text-white/70">
                    {{ formatDate(user.createdAt) }}
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex items-center space-x-2">
                      <!-- Expand button -->
                      <button 
                        class="text-purple-400 hover:text-purple-300 transition-colors"
                        @click.stop="toggleUserDetails(user.id)"
                      >
                        <svg 
                          :class="['w-5 h-5 transition-transform', expandedUsers.includes(user.id) ? 'rotate-180' : '']"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Expanded Details Row -->
                <tr v-if="expandedUsers.includes(user.id)" class="bg-white/5">
                  <td colspan="6" class="px-6 py-6">
                    <div class="space-y-6">
                      <!-- Basic Information -->
                      <div>
                        <h4 class="text-lg font-semibold text-white mb-3">Basic Information</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-white/70 mb-1">Email</label>
                            <p class="text-white">{{ user.email }}</p>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-white/70 mb-1">Full Name</label>
                            <p class="text-white">
                              {{ user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'Not provided' }}
                            </p>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-white/70 mb-1">Channel URL</label>
                            <template v-if="user.youtubeChannel && isValidUrl(user.youtubeChannel)">
                              <a 
                                :href="user.youtubeChannel" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                class="text-blue-400 hover:text-blue-300 underline transition-colors break-all inline-flex items-center gap-1"
                              >
                                {{ user.youtubeChannel }}
                                <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            </template>
                            <template v-else>
                              <p class="text-white">{{ user.youtubeChannel || 'Not provided' }}</p>
                            </template>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-white/70 mb-1">Registration Date</label>
                            <p class="text-white">{{ formatDate(user.createdAt) }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- Onboarding Data -->
                      <div v-if="user.additionalQuestionsData">
                        <h4 class="text-lg font-semibold text-white mb-3">Onboarding Information</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div v-for="(value, key) in parseOnboardingData(user.additionalQuestionsData)" :key="key">
                            <label class="block text-sm font-medium text-white/70 mb-1">
                              {{ formatFieldName(key) }}
                            </label>
                            <p class="text-white">
                              {{ formatFieldValue(value) }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- No Onboarding Data -->
                      <div v-else>
                        <h4 class="text-lg font-semibold text-white mb-3">Onboarding Information</h4>
                        <p class="text-white/70 italic">User has not completed onboarding yet.</p>
                      </div>

                      <!-- Admin Review Section -->
                      <div v-if="user.profileCompleted && user.additionalQuestionsData" class="mt-6">
                        <!-- Show existing admin review if it exists -->
                        <div v-if="getAdminReview(user) && !isEditingReview(user.id)">
                          <div class="flex justify-between items-center mb-3">
                            <h4 class="text-lg font-semibold text-white">Admin Review</h4>
                            <button
                              @click="startEditingReview(user.id)"
                              class="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                            >
                              Edit Review
                            </button>
                          </div>
                          <div class="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <div class="space-y-4">
                              <div>
                                <label class="block text-sm font-medium text-blue-300 mb-1">Channel Description</label>
                                <p class="text-white break-words whitespace-pre-wrap">{{ getAdminReview(user).channelDescription }}</p>
                              </div>
                              <!-- AI Training Metrics -->
                              <div class="mb-4">
                                <h5 class="text-sm font-semibold text-blue-200 mb-3">AI Training Metrics</h5>
                                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Copyright Amount</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-500/20 text-blue-300">
                                      {{ getAdminReview(user).copyrightAmount }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Production Quality</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-300">
                                      {{ getAdminReview(user).productionQuality }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Video Consistency</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-500/20 text-purple-300">
                                      {{ getAdminReview(user).videoResolutionConsistency }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Audio Clarity</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-300">
                                      {{ getAdminReview(user).audioClarity }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Instructional Value</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-500/20 text-indigo-300">
                                      {{ getAdminReview(user).instructionalValue }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Visual Complexity</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-pink-500/20 text-pink-300">
                                      {{ getAdminReview(user).visualComplexity }}/5
                                    </span>
                                  </div>
                                  <div>
                                    <label class="block text-xs font-medium text-blue-300 mb-1">Editing Sophistication</label>
                                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-300">
                                      {{ getAdminReview(user).editingSophistication }}/5
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <!-- Review Metadata -->
                              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-blue-500/20">
                                <div>
                                  <label class="block text-xs font-medium text-blue-300 mb-1">Reviewed By</label>
                                  <p class="text-white text-sm">{{ getAdminReview(user).reviewedBy }}</p>
                                </div>
                                <div>
                                  <label class="block text-xs font-medium text-blue-300 mb-1">Reviewed At</label>
                                  <p class="text-white text-sm">{{ formatDate(getAdminReview(user).reviewedAt) }}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Show admin review form if not yet reviewed OR if editing existing review -->
                        <div v-else-if="(!user.accepted && !user.rejectedAt) || isEditingReview(user.id) || (!getAdminReview(user) && (user.accepted || user.rejectedAt))">
                          <div class="flex justify-between items-center mb-3">
                            <h4 class="text-lg font-semibold text-white">
                              {{ getAdminReview(user) ? 'Edit Admin Review' : (!user.accepted && !user.rejectedAt) ? 'Admin Review' : 'Add Admin Review (Backfill)' }}
                            </h4>
                            <button
                              v-if="isEditingReview(user.id)"
                              @click="cancelEditingReview(user.id)"
                              class="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                          <!-- Backfill Notice -->
                          <div v-if="!getAdminReview(user) && (user.accepted || user.rejectedAt) && !isEditingReview(user.id)" class="mb-4">
                            <div class="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                              <p class="text-yellow-200 text-sm">
                                <span class="font-medium">Backfill Mode:</span> This user was processed before admin reviews were implemented. Adding review data for AI training purposes.
                              </p>
                            </div>
                          </div>
                          <div class="bg-white/5 border border-white/20 rounded-lg p-4">
                                                         <div class="space-y-4">
                              <!-- Channel Description -->
                              <div>
                                <label for="channelDescription" class="block text-sm font-medium text-white/70 mb-2">
                                  Channel Description *
                                </label>
                                <textarea
                                  v-model="getReviewForm(user.id).channelDescription"
                                  required
                                  rows="3"
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                  placeholder="Describe the channel content, style, and audience..."
                                ></textarea>
                              </div>

                              <!-- Amount of Copyright -->
                              <div>
                                <label for="copyrightAmount" class="block text-sm font-medium text-white/70 mb-2">
                                  Amount of Copyright (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).copyrightAmount"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select copyright level</option>
                                  <option value="1">1 - Very Low</option>
                                  <option value="2">2 - Low</option>
                                  <option value="3">3 - Medium</option>
                                  <option value="4">4 - High</option>
                                  <option value="5">5 - Very High</option>
                                </select>
                              </div>

                              <!-- Production Quality -->
                              <div>
                                <label for="productionQuality" class="block text-sm font-medium text-white/70 mb-2">
                                  Production Quality (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).productionQuality"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select production quality level</option>
                                  <option value="1">1 - Very Low</option>
                                  <option value="2">2 - Low</option>
                                  <option value="3">3 - Medium</option>
                                  <option value="4">4 - High</option>
                                  <option value="5">5 - Very High</option>
                                </select>
                              </div>

                              <!-- Video Resolution Consistency -->
                              <div>
                                <label for="videoResolutionConsistency" class="block text-sm font-medium text-white/70 mb-2">
                                  Video Resolution Consistency (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).videoResolutionConsistency"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select consistency level</option>
                                  <option value="1">1 - Very Inconsistent</option>
                                  <option value="2">2 - Mostly Inconsistent</option>
                                  <option value="3">3 - Somewhat Consistent</option>
                                  <option value="4">4 - Mostly Consistent</option>
                                  <option value="5">5 - Very Consistent</option>
                                </select>
                              </div>

                              <!-- Audio Clarity -->
                              <div>
                                <label for="audioClarity" class="block text-sm font-medium text-white/70 mb-2">
                                  Audio Clarity (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).audioClarity"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select audio clarity level</option>
                                  <option value="1">1 - Very Poor (Muffled/Noisy)</option>
                                  <option value="2">2 - Poor (Background Noise)</option>
                                  <option value="3">3 - Fair (Some Issues)</option>
                                  <option value="4">4 - Good (Clear)</option>
                                  <option value="5">5 - Excellent (Crystal Clear)</option>
                                </select>
                              </div>

                              <!-- Instructional Value -->
                              <div>
                                <label for="instructionalValue" class="block text-sm font-medium text-white/70 mb-2">
                                  Instructional Value (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).instructionalValue"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select instructional value level</option>
                                  <option value="1">1 - No Teaching Value</option>
                                  <option value="2">2 - Limited Teaching Value</option>
                                  <option value="3">3 - Some Teaching Value</option>
                                  <option value="4">4 - Good Teaching Value</option>
                                  <option value="5">5 - Excellent Teaching Value</option>
                                </select>
                              </div>

                              <!-- Visual Complexity -->
                              <div>
                                <label for="visualComplexity" class="block text-sm font-medium text-white/70 mb-2">
                                  Visual Complexity (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).visualComplexity"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select visual complexity level</option>
                                  <option value="1">1 - Very Simple (Single Subject)</option>
                                  <option value="2">2 - Simple (Few Elements)</option>
                                  <option value="3">3 - Moderate (Multiple Elements)</option>
                                  <option value="4">4 - Complex (Many Elements)</option>
                                  <option value="5">5 - Very Complex (Multi-layered)</option>
                                </select>
                              </div>

                              <!-- Editing Sophistication -->
                              <div>
                                <label for="editingSophistication" class="block text-sm font-medium text-white/70 mb-2">
                                  Editing Sophistication (1-5 scale) *
                                </label>
                                <select
                                  v-model="getReviewForm(user.id).editingSophistication"
                                  required
                                  class="w-full px-3 py-2 bg-white/10 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="">Select editing sophistication level</option>
                                  <option value="1">1 - Basic (Simple Cuts)</option>
                                  <option value="2">2 - Minimal (Basic Transitions)</option>
                                  <option value="3">3 - Intermediate (Some Effects)</option>
                                  <option value="4">4 - Advanced (Professional Effects)</option>
                                  <option value="5">5 - Expert (Complex Post-production)</option>
                                </select>
                              </div>

                              <!-- Error Message -->
                              <div v-if="getReviewError(user.id)" class="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                                <p class="text-red-200 text-sm">{{ getReviewError(user.id) }}</p>
                              </div>

                              <!-- Action Buttons -->
                              <div class="flex justify-end space-x-3 pt-4">
                                <!-- For new applications (not yet accepted/rejected) -->
                                <template v-if="!user.accepted && !user.rejectedAt">
                                  <button
                                    type="button"
                                    @click="submitReviewWithAction(user.id, 'reject')"
                                    :disabled="isReviewLoading(user.id)"
                                    class="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                                  >
                                    <span v-if="!isReviewLoading(user.id)">Reject Application</span>
                                    <span v-else class="flex items-center">
                                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Processing...
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    @click="submitReviewWithAction(user.id, 'accept')"
                                    :disabled="isReviewLoading(user.id)"
                                    class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                                  >
                                    <span v-if="!isReviewLoading(user.id)">Accept Application</span>
                                    <span v-else class="flex items-center">
                                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 7 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Processing...
                                    </span>
                                  </button>
                                </template>
                                
                                <!-- For backfilling existing applications (already accepted/rejected) -->
                                <template v-else>
                                  <button
                                    type="button"
                                    @click="saveAdminReviewOnly(user.id)"
                                    :disabled="isReviewLoading(user.id)"
                                    class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                                  >
                                    <span v-if="!isReviewLoading(user.id)">Save Admin Review</span>
                                    <span v-else class="flex items-center">
                                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 7 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Processing...
                                    </span>
                                  </button>
                                </template>
                              </div>
                             </div>
                          </div>
                        </div>
                      </div>

                      <!-- Danger Zone - Delete User -->
                      <div class="mt-8 pt-6 border-t border-red-500/30">
                        <h4 class="text-lg font-semibold text-red-400 mb-3 flex items-center">
                          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          Danger Zone
                        </h4>
                        <div class="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <p class="text-red-200 text-sm mb-3">
                            <strong>Warning:</strong> Deleting a user will permanently remove all their data from the system. This action cannot be undone.
                          </p>
                          <button
                            @click="showDeleteConfirmation(user.id, user.email)"
                            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center"
                          >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete User Permanently
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="users.length === 0" class="text-center py-12">
          <p class="text-white/70">No users found.</p>
        </div>
      </div>
    </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteConfirmation.show" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-black opacity-75"></div>
        </div>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-gradient-to-br from-[#1a1a5c] to-[#2a2a7c] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-red-500/30">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center">
              <div class="flex-shrink-0 w-10 h-10 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg leading-6 font-medium text-red-400">
                  Delete User Permanently
                </h3>
                <p class="mt-1 text-sm text-white/70">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="px-6 pb-4">
            <div class="text-sm">
              <p class="text-white mb-4">
                You are about to permanently delete the user:
              </p>
              <div class="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p class="text-red-200 font-medium">{{ deleteConfirmation.userEmail }}</p>
                <p class="text-red-300/70 text-xs mt-1">User ID: {{ deleteConfirmation.userId }}</p>
              </div>
              <p class="text-white/90 mb-4">
                <strong>This will permanently delete:</strong>
              </p>
              <ul class="text-white/70 text-sm list-disc list-inside space-y-1 mb-4">
                <li>User account and profile information</li>
                <li>All onboarding and application data</li>
                <li>Admin reviews and notes</li>
                <li>All associated records in the database</li>
              </ul>
              <div class="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <p class="text-yellow-200 text-sm">
                  <strong>Verification Required:</strong> To confirm this deletion, type <code class="bg-yellow-500/20 px-1 rounded">DELETE_USER_PERMANENTLY</code> in the field below:
                </p>
              </div>
              <input
                v-model="deleteConfirmation.confirmationText"
                type="text"
                placeholder="Type: DELETE_USER_PERMANENTLY"
                class="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="bg-white/5 px-6 py-3 sm:flex sm:flex-row-reverse">
            <button
              @click="confirmDeleteUser"
              :disabled="deleteConfirmation.confirmationText !== 'DELETE_USER_PERMANENTLY' || deleteConfirmation.loading"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="!deleteConfirmation.loading">Delete Permanently</span>
              <span v-else class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Deleting...
              </span>
            </button>
            <button
              @click="hideDeleteConfirmation"
              :disabled="deleteConfirmation.loading"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-white/30 shadow-sm px-4 py-2 bg-white/10 text-base font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { apiCall } from '../config/api.js'
import AdminAnalytics from '../components/AdminAnalytics.vue'

export default {
  name: 'AdminBackoffice',
  components: {
    AdminAnalytics
  },
  data() {
    return {
      activeTab: 'analytics', // Default to analytics tab
      users: [],
      loading: true,
      error: null,
      expandedUsers: [],
      adminEmail: 'nancy@rendery.org',
      reviewForms: {}, // Store review forms for each user by userId
      reviewLoading: {}, // Store loading states for each user by userId
      reviewErrors: {}, // Store errors for each user by userId
      editingReviews: {}, // Store editing states for each user by userId
      deleteConfirmation: {
        show: false,
        userId: null,
        userEmail: '',
        confirmationText: '',
        loading: false
      }
    }
  },
  async mounted() {
    // Add a small delay to ensure auth state is properly set
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Ensure auth state is initialized
    this.ensureAuthInitialized();
    
    await this.checkAdminAccess();
    await this.fetchUsers();
  },
  methods: {
    ensureAuthInitialized() {
      // If auth state is empty but localStorage has data, initialize it
      if (!this.$authState.user && !this.$authState.token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth state empty, attempting to initialize from localStorage');
        }
        this.$authActions.initAuth();
      }
    },
    async checkAdminAccess() {
      // Double-check authentication at component level (defense in depth)
      const user = this.$authState.user;
      const token = this.$authState.token;
      
      if (!user || !token) {
        // Silent security cleanup - don't log in production
        if (process.env.NODE_ENV === 'development') {
          console.warn('Security: No authentication found in admin component');
        }
        this.$authActions.clearAuth();
        this.$router.push('/');
        return;
      }
      
      if (user.email !== 'nancy@rendery.org') {
        // Silent security redirect - don't log user email in production
        if (process.env.NODE_ENV === 'development') {
          console.warn('Security: Non-admin user attempted to access admin interface:', user.email);
        }
        this.$router.push('/');
        return;
      }
      
      // Verify token is still valid with backend
      try {
        const { response } = await apiCall('auth/verify', {
          method: 'POST',
        });
        
        if (!response.ok) {
          // Handle rate limiting gracefully - don't clear auth on 429 errors
          if (response.status === 429) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Token verification rate limited - proceeding without verification');
            }
            return; // Skip verification but don't clear auth
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.warn('Security: Token verification failed');
          }
          this.$authActions.clearAuth();
          this.$router.push('/');
          return;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Security: Token verification error:', error);
        }
        this.$authActions.clearAuth();
        this.$router.push('/');
        return;
      }
    },
    async fetchUsers() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = this.$authState.token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const { response, data } = await apiCall('users/admin/all', {
          method: 'GET',
        });

        if (response.ok && data.success) {
          this.users = data.users;
        } else {
          throw new Error(data.message || 'Failed to fetch users');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching users:', error);
        }
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    toggleUserDetails(userId) {
      const index = this.expandedUsers.indexOf(userId);
      if (index > -1) {
        this.expandedUsers.splice(index, 1);
      } else {
        this.expandedUsers.push(userId);
      }
    },
    getUserInitials(user) {
      if (user.firstName && user.lastName) {
        return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
      }
      return user.email.charAt(0).toUpperCase();
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    parseOnboardingData(data) {
      try {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        
        // Remove adminReview from the onboarding data display
        if (parsedData && parsedData.adminReview) {
          const onboardingData = { ...parsedData };
          delete onboardingData.adminReview;
          return onboardingData;
        }
        
        return parsedData;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error parsing onboarding data:', error);
        }
        return {};
      }
    },
    formatFieldName(key) {
      const fieldNames = {
        subscriberCount: 'Subscriber Count',
        originalCreator: 'Original Creator',
        thirdPartyContent: 'Third Party Content',
        peopleSpeaking: 'People Speaking',
        primaryLanguage: 'Primary Language',
        multipleLanguages: 'Multiple Languages',
        productionCountry: 'Production Country',
        productionCity: 'Production City',
        ageRestrictions: 'Age Restrictions',
        aiGenerated: 'AI Generated',
        channelCategories: 'Channel Categories'
      };
      return fieldNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    },
    formatFieldValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      return value || 'Not provided';
    },
    logout() {
      this.$authActions.clearAuth();
      this.$router.push('/');
    },
    getApplicationStatusClass(user) {
      // If user hasn't completed onboarding, show as not applicable
      if (!user.profileCompleted || !user.additionalQuestionsData) {
        return 'bg-gray-500/20 text-gray-400';
      }
      
      // For completed profiles, check acceptance status
      if (user.accepted === true) {
        return 'bg-green-500/20 text-green-400';
      } else if (user.rejectedAt) {
        return 'bg-red-500/20 text-red-400';
      } else {
        // Default false means pending
        return 'bg-yellow-500/20 text-yellow-400';
      }
    },
    getApplicationStatusText(user) {
      // If user hasn't completed onboarding, show as not applicable
      if (!user.profileCompleted || !user.additionalQuestionsData) {
        return 'N/A';
      }
      
      // For completed profiles, check acceptance status
      if (user.accepted === true) {
        return 'Accepted';
      } else if (user.rejectedAt) {
        return 'Rejected';
      } else {
        // Default false means pending
        return 'Pending';
      }
    },
    async acceptUser(userId) {
      try {
        const token = this.$authState.token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const { response, data } = await apiCall(`users/admin/${userId}/accept`, {
          method: 'PUT',
        });

        if (response.ok && data.success) {
          // Update the user in the local array
          const userIndex = this.users.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            this.users[userIndex].accepted = true;
            this.users[userIndex].rejectedAt = null;
          }
        } else {
          throw new Error(data.message || 'Failed to accept user');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error accepting user:', error);
        }
        alert('Failed to accept user: ' + error.message);
      }
    },
    async rejectUser(userId) {
      try {
        const token = this.$authState.token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const { response, data } = await apiCall(`users/admin/${userId}/reject`, {
          method: 'PUT',
        });

        if (response.ok && data.success) {
          // Update the user in the local array
          const userIndex = this.users.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            this.users[userIndex].accepted = false;
            this.users[userIndex].rejectedAt = data.user.rejectedAt;
          }
        } else {
          throw new Error(data.message || 'Failed to reject user');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error rejecting user:', error);
        }
        alert('Failed to reject user: ' + error.message);
      }
    },
         isValidUrl(url) {
       try {
         const urlObj = new URL(url);
         // Check if it's a valid URL with http/https protocol
         return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
       } catch (_) {
         return false;
       }
     },
         formatChannelDisplay(url) {
       try {
         const urlObj = new URL(url);
         const path = urlObj.pathname;
         
         // Handle different YouTube URL formats
         if (path.includes('/channel/')) {
           // Extract channel ID for /channel/ URLs
           const channelId = path.split('/channel/')[1];
           return channelId ? `Channel: ${channelId.substring(0, 20)}...` : 'YouTube Channel';
         } else if (path.includes('/c/') || path.includes('/user/')) {
           // Extract custom name for /c/ or /user/ URLs
           const parts = path.split('/').filter(part => part);
           const channelName = parts[parts.length - 1];
           return channelName.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
         } else if (path.includes('/@')) {
           // Handle new @username format
           const username = path.replace('/@', '');
           return `@${username}`;
         } else {
           // Fallback for other formats
           const parts = path.split('/').filter(part => part);
           if (parts.length > 0) {
             const lastPart = parts[parts.length - 1];
             return lastPart.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
           }
           return 'YouTube Channel';
         }
       } catch (error) {
         // If URL parsing fails, just show a generic label
         return 'YouTube Channel';
       }
    },
    getReviewForm(userId) {
      if (!this.reviewForms[userId]) {
        this.reviewForms[userId] = {
          channelDescription: '',
          copyrightAmount: '',
          productionQuality: '',
          videoResolutionConsistency: '',
          audioClarity: '',
          instructionalValue: '',
          visualComplexity: '',
          editingSophistication: ''
        };
      }
      return this.reviewForms[userId];
    },
    getReviewError(userId) {
      return this.reviewErrors[userId] || null;
    },
    isReviewLoading(userId) {
      return this.reviewLoading[userId] || false;
    },
    async submitReviewWithAction(userId, action) {
      this.reviewLoading[userId] = true;
      this.reviewErrors[userId] = null;
      
      try {
        const reviewForm = this.getReviewForm(userId);
        
        // Validate form
        if (!reviewForm.channelDescription.trim()) {
          throw new Error('Channel description is required');
        }
        if (!reviewForm.copyrightAmount) {
          throw new Error('Copyright amount is required');
        }
        if (!reviewForm.productionQuality) {
          throw new Error('Production quality is required');
        }
        if (!reviewForm.videoResolutionConsistency) {
          throw new Error('Video resolution consistency is required');
        }
        if (!reviewForm.audioClarity) {
          throw new Error('Audio clarity is required');
        }
        if (!reviewForm.instructionalValue) {
          throw new Error('Instructional value is required');
        }
        if (!reviewForm.visualComplexity) {
          throw new Error('Visual complexity is required');
        }
        if (!reviewForm.editingSophistication) {
          throw new Error('Editing sophistication is required');
        }

        const token = this.$authState.token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const { response, data } = await apiCall(`users/admin/${userId}/review`, {
          method: 'PUT',
          body: JSON.stringify({
            action: action,
            adminReview: {
              channelDescription: reviewForm.channelDescription,
              copyrightAmount: parseInt(reviewForm.copyrightAmount),
              productionQuality: parseInt(reviewForm.productionQuality),
              videoResolutionConsistency: parseInt(reviewForm.videoResolutionConsistency),
              audioClarity: parseInt(reviewForm.audioClarity),
              instructionalValue: parseInt(reviewForm.instructionalValue),
              visualComplexity: parseInt(reviewForm.visualComplexity),
              editingSophistication: parseInt(reviewForm.editingSophistication),
              reviewedBy: this.$authState.user.email,
              reviewedAt: new Date().toISOString()
            }
          })
        });

        if (response.ok && data.success) {
          // Update the user in the local array
          const userIndex = this.users.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            // Update the user's additionalQuestionsData with admin review
            this.users[userIndex].additionalQuestionsData = data.user.additionalQuestionsData;
            
            if (action === 'accept') {
              this.users[userIndex].accepted = true;
              this.users[userIndex].rejectedAt = null;
            } else {
              this.users[userIndex].accepted = false;
              this.users[userIndex].rejectedAt = data.user.rejectedAt;
            }
          }
          
          // Clear the form after successful submission
          this.reviewForms[userId] = {
            channelDescription: '',
            copyrightAmount: '',
            productionQuality: '',
            videoResolutionConsistency: '',
            audioClarity: '',
            instructionalValue: '',
            visualComplexity: '',
            editingSophistication: ''
          };
        } else {
          throw new Error(data.message || 'Failed to submit review');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error submitting review:', error);
        }
        this.reviewErrors[userId] = error.message;
      } finally {
        this.reviewLoading[userId] = false;
      }
    },
    getAdminReview(user) {
      if (user.additionalQuestionsData) {
        const data = typeof user.additionalQuestionsData === 'string' ? JSON.parse(user.additionalQuestionsData) : user.additionalQuestionsData;
        return data.adminReview;
      }
      return null;
    },
    isEditingReview(userId) {
      return this.editingReviews[userId] || false;
    },
    startEditingReview(userId) {
      this.editingReviews[userId] = true;
      
      // Initialize form with existing data if available
      const user = this.users.find(u => u.id === userId);
      if (user) {
        const existingReview = this.getAdminReview(user);
        if (existingReview) {
          this.reviewForms[userId] = {
            channelDescription: existingReview.channelDescription || '',
            copyrightAmount: existingReview.copyrightAmount?.toString() || '',
            productionQuality: existingReview.productionQuality?.toString() || '',
            videoResolutionConsistency: existingReview.videoResolutionConsistency?.toString() || '',
            audioClarity: existingReview.audioClarity?.toString() || '',
            instructionalValue: existingReview.instructionalValue?.toString() || '',
            visualComplexity: existingReview.visualComplexity?.toString() || '',
            editingSophistication: existingReview.editingSophistication?.toString() || ''
          };
        } else {
          // Initialize empty form for new review
          this.getReviewForm(userId);
        }
      }
    },
    cancelEditingReview(userId) {
      this.editingReviews[userId] = false;
      // Clear any form data
      this.reviewForms[userId] = {
        channelDescription: '',
        copyrightAmount: '',
        productionQuality: '',
        videoResolutionConsistency: '',
        audioClarity: '',
        instructionalValue: '',
        visualComplexity: '',
        editingSophistication: ''
      };
      // Clear any errors
      this.reviewErrors[userId] = null;
    },
    async saveAdminReviewOnly(userId) {
      this.reviewLoading[userId] = true;
      this.reviewErrors[userId] = null;
      
      try {
        const reviewForm = this.getReviewForm(userId);
        
        // Validate form
        if (!reviewForm.channelDescription.trim()) {
          throw new Error('Channel description is required');
        }
        if (!reviewForm.copyrightAmount) {
          throw new Error('Copyright amount is required');
        }
        if (!reviewForm.productionQuality) {
          throw new Error('Production quality is required');
        }
        if (!reviewForm.videoResolutionConsistency) {
          throw new Error('Video resolution consistency is required');
        }
        if (!reviewForm.audioClarity) {
          throw new Error('Audio clarity is required');
        }
        if (!reviewForm.instructionalValue) {
          throw new Error('Instructional value is required');
        }
        if (!reviewForm.visualComplexity) {
          throw new Error('Visual complexity is required');
        }
        if (!reviewForm.editingSophistication) {
          throw new Error('Editing sophistication is required');
        }

        const token = this.$authState.token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const { response, data } = await apiCall(`users/admin/${userId}/reviewonly`, {
          method: 'PUT',
          body: JSON.stringify({
            adminReview: {
              channelDescription: reviewForm.channelDescription,
              copyrightAmount: parseInt(reviewForm.copyrightAmount),
              productionQuality: parseInt(reviewForm.productionQuality),
              videoResolutionConsistency: parseInt(reviewForm.videoResolutionConsistency),
              audioClarity: parseInt(reviewForm.audioClarity),
              instructionalValue: parseInt(reviewForm.instructionalValue),
              visualComplexity: parseInt(reviewForm.visualComplexity),
              editingSophistication: parseInt(reviewForm.editingSophistication),
              reviewedBy: this.$authState.user.email,
              reviewedAt: new Date().toISOString()
            }
          })
        });

        if (response.ok && data.success) {
          // Update the user in the local array
          const userIndex = this.users.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            // Update the user's additionalQuestionsData with admin review
            this.users[userIndex].additionalQuestionsData = data.user.additionalQuestionsData;
          }
          
          // Stop editing mode
          this.editingReviews[userId] = false;
          
          // Clear the form after successful submission
          this.reviewForms[userId] = {
            channelDescription: '',
            copyrightAmount: '',
            productionQuality: '',
            videoResolutionConsistency: '',
            audioClarity: '',
            instructionalValue: '',
            visualComplexity: '',
            editingSophistication: ''
          };
        } else {
          throw new Error(data.message || 'Failed to save admin review');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error saving admin review:', error);
        }
        this.reviewErrors[userId] = error.message;
      } finally {
        this.reviewLoading[userId] = false;
      }
    },
    showDeleteConfirmation(userId, userEmail) {
      this.deleteConfirmation = {
        show: true,
        userId: userId,
        userEmail: userEmail,
        confirmationText: '',
        loading: false
      };
    },
    hideDeleteConfirmation() {
      this.deleteConfirmation = {
        show: false,
        userId: null,
        userEmail: '',
        confirmationText: '',
        loading: false
      };
    },
    async confirmDeleteUser() {
      if (this.deleteConfirmation.confirmationText !== 'DELETE_USER_PERMANENTLY') {
        return;
      }

      this.deleteConfirmation.loading = true;

      try {
        const { response, data } = await apiCall(`users/admin/${this.deleteConfirmation.userId}`, {
          method: 'DELETE',
          body: JSON.stringify({
            confirmation: 'DELETE_USER_PERMANENTLY'
          })
        });

        if (response.ok && data.success) {
          // Remove the user from the local array
          const userIndex = this.users.findIndex(u => u.id === this.deleteConfirmation.userId);
          if (userIndex !== -1) {
            this.users.splice(userIndex, 1);
          }

          // Close the expanded user details if it was open
          const expandedIndex = this.expandedUsers.indexOf(this.deleteConfirmation.userId);
          if (expandedIndex !== -1) {
            this.expandedUsers.splice(expandedIndex, 1);
          }

          // Hide the confirmation modal
          this.hideDeleteConfirmation();

          // Show success message
          alert(`User ${this.deleteConfirmation.userEmail} has been permanently deleted from the system.`);
        } else {
          throw new Error(data.message || 'Failed to delete user');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error deleting user:', error);
        }
        alert('Failed to delete user: ' + error.message);
      } finally {
        this.deleteConfirmation.loading = false;
      }
    }
  }
}
</script> 